import CalendarService from 'src/services/calendar'
import RecurrenceService from 'src/services/recurrences'
import CalendarEvent from '@/models/CalendarEvent.model'
import ActionPlanEvent from '@/models/ActionPlanEvent.model'
import moment from 'moment'

// Las réplicas de invitados comparten `id` pero difieren en `triggeredBy`, y los
// action plans pueden repetir el `id` de un evento — la llave lleva las tres partes.
const eventStateKey = (event) => `${event.type}-${event.id}-${event.triggeredBy}`

export default {
  fetchAll (payload) {
    this.loadingTasks = true
    if (!payload.stay) {
      this.tasks = []
      this.events = []
      this.fetchAllRequestId += 1
    }
    const requestId = this.fetchAllRequestId
    const events = []
    return CalendarService.fetchAll(payload)
      .then(data => {
        // Latest-wins: solo la respuesta del fetch sin `stay` más reciente escribe.
        if (!payload.stay && requestId !== this.fetchAllRequestId) return

        const isSingleUser = payload.isSingleUser
        if (!isSingleUser || (isSingleUser && this.selectedUsers.some(user => user.id === payload.user_id[0]))) {
          data.events.forEach(event => {
            const _event = new CalendarEvent(event)
            events.push(_event.attributes)
          })
          data.action_plans.forEach(event => {
            const _event = new ActionPlanEvent(event)
            events.push(_event.attributes)
          })
        }

        // Los fetch con `stay: true` se acumulan: se filtra lo ya presente en el state.
        const seenKeys = new Set(this.events.map(eventStateKey))
        const newEvents = events.filter(event => {
          const key = eventStateKey(event)
          if (seenKeys.has(key)) return false
          seenKeys.add(key)
          return true
        })

        this.events.push(...newEvents)
      })
      .catch(err => {
        console.error(err)
        throw err
      })
      .finally(() => {
        if (payload.stay || requestId === this.fetchAllRequestId) {
          this.loading = false
          this.loadingTasks = false
        }
      })
  },
  removeUserEvents (userId) {
    this.events = this.events.filter(event => event.triggeredBy !== userId)
  },
  fetchRecurrence (recurrenceId) {
    const promise = RecurrenceService.fetch(recurrenceId)

    promise.then(data => {
      this.recurrence = data
    })

    return promise
  },
  create (event) {
    const promise = event.recurrence !== null ? CalendarService.createRecurrence(event) : CalendarService.create(event)

    promise.then(data => {
      if (event.recurrence === null) {
        data.events.forEach(event => {
          if (event.assigned_to === event.created_by) {
            const _event = new CalendarEvent(event)
            this.events.push(_event.attributes)
          }
        })
      }
    })

    return promise
  },
  update (event) {
    let payload = event
    if (event.recurrence_id || event.recurrence) {
      event.recurrence.time = event.time
      event.recurrence.allDay = event.allDay
      event.recurrence.startDate = event.dates[0]

      if (event.recurrence_id) {
        payload = {
          event_id: event.id,
          recurrence_id: event.recurrence_id,
          model: 'Event',
          base_model: event,
          recurrence: event.recurrence,
          end_date: event.recurrence.endDate
        }
      }
    }
    const promise = event.recurrence_id ? CalendarService.updateRecurrence(payload) : CalendarService.updateEvent(payload)

    promise.then(data => {
      if (event.recurrence === null) {
        const events = this.events
        data.events.forEach(event => {
          if (event.assigned_to === event.created_by) {
            const _event = new CalendarEvent(event)
            events.push(_event.attributes)
          }
        })

        this.events = this.events.filter(e => e.id !== event.id)
      } else {
        this.events = this.events.filter(e => e.recurrence_id !== event.recurrence_id)
      }
    })

    return promise
  },
  updateEvent (payload) {
    const promise = CalendarService.update(payload)

    promise.then(data => {
      if (payload.updateGroup) {
        this.events = this.events.filter(e => e.group_id !== payload.group_id)
      } else {
        const index = this.events.findIndex(e => e.id === payload.id)
        this.events.splice(index, 1)
      }

      data.events.forEach(event => {
        const _event = new CalendarEvent(event)
        this.events.push(_event.attributes)
      })
    })

    return promise
  },
  updateForOtherEvent (payload) {
    const promise = CalendarService.updateForOtherEvent(payload)

    promise.then(data => {
      const event = new CalendarEvent(data.data)
      // Remove the old event from the list
      const index = this.events.findIndex(e => e.id === event.id)
      this.events.splice(index, 1)
      this.events.push(event.attributes)
    })

    return promise
  },
  deleteEvent (id) {
    const promise = CalendarService.delete(id)

    promise.then(() => {
      const index = this.events.findIndex(event => event.id === id)
      this.events.splice(index, 1)
    }).catch((err) => {
      console.error(err)
    })

    return promise
  },
  deleteEventGroup (event) {
    const promise = CalendarService.deleteGroup(event.id)

    promise.then(() => {
      this.events = this.events.filter(e => e.group_id !== event.group_id)
    }).catch((err) => {
      console.error(err)
    })

    return promise
  },
  deleteEventRecurrence (event) {
    const promise = CalendarService.deleteEventRecurrence(event.id)

    promise.then(() => {
      this.events = this.events.filter(e => e.recurrence_id !== event.recurrence_id)
    }).catch((err) => {
      console.error(err)
    })

    return promise
  },
  toggleStatus (id) {
    const promise = CalendarService.toggleStatus(id)

    promise.then((data) => {
      const index = this.events.findIndex(e => e.id === id)
      this.events.splice(index, 1)

      const _event = new CalendarEvent(data.event)
      this.events.push(_event.attributes)
    })

    return promise
  },
  // New Calendar
  setSelectedUsers (users) {
    this.selectedUsers = users
  },
  setTitle (title) {
    this.newEvent.title = title
  },
  setSummary (summary) {
    this.newEvent.summary = summary
  },
  setRange (range) {
    this.newEvent.range = range
  },
  setType (type) {
    this.newEvent.type = type
  },
  setPerDay (perDay) {
    this.newEvent.perDay = perDay
  },
  setAllDay (allDay) {
    this.newEvent.time.start = null
    this.newEvent.time.end = null
    this.newEvent.allDay = allDay
  },
  setDates (dates) {
    this.newEvent.dates = dates
  },
  setTimeStart (time) {
    this.newEvent.time.start = time
  },
  setTimeEnd (time) {
    this.newEvent.time.end = time
  },
  setFormId (formId) {
    this.newEvent.formId = formId
  },
  setForms (forms) {
    this.newEvent.forms = forms
  },
  setLocationId (location) {
    if (location?.id) {
      this.newEvent.locationId = location ? location.id : null
      this.newEvent.location = location
    } else {
      this.newEvent.locationId = location
      this.newEvent.location = null
    }
  },
  setLocations (locations) {
    this.newEvent.locations = locations
  },
  setRole (role) {
    this.newEvent.role = role
  },
  setSendNotification (sendNotification) {
    this.newEvent.sendNotification = sendNotification
  },
  setGuests (guests) {
    this.newEvent.guests = guests
  },
  setForOthers (forOthers) {
    this.newEvent.forOthers = forOthers
  },
  setEventRecurrence (recurrence) {
    this.newEvent.recurrence = recurrence
  },
  setMeetLink (meetLink) {
    this.newEvent.meetLink = meetLink
  },
  setLink (link) {
    this.newEvent.link = link
  },
  setFile (file) {
    this.newEvent.file = file
  },
  setUsersId (usersId) {
    this.newEvent.usersId = usersId
  },
  getGuests (eventId) {
    CalendarService.getGuests(eventId).then(data => {
      this.newEvent.guests = data
    })
  },
  setWait (value) {
    this.newEvent.wait = value
  },
  async getSetLocations (eventId) {
    try {
      const data = await CalendarService.getSetLocations(eventId)
      const usersIds = data.flatMap(set => set.userId)
      if (usersIds.length > 0) this.newEvent.wait = true

      return { locations: data, ids: usersIds }
    } catch (error) {
      console.error('Error fetching locations:', error)
      return { locations: [], ids: [] }
    }
  },
  getRecurrence (recurrenceId) {
    RecurrenceService.getRecurrence(recurrenceId).then(data => {
      this.newEvent.recurrence = data
    })
  },
  resetNewEvent () {
    this.newEvent = {
      title: '',
      summary: null,
      range: null,
      type: null,
      perDay: null,
      allDay: false,
      dates: [],
      time: {
        start: null,
        end: null
      },
      formId: null,
      forms: null,
      locationId: null,
      location: null,
      locations: [],
      role: null,
      sendNotification: false,
      guests: [],
      forOthers: false,
      recurrence: null,
      meetLink: null,
      link: null,
      file: null,
      usersId: []
    }
  },
  setCurrentEvent (event) {
    const allDay = !event.duration || event.duration === 0
    const type = event.event_type === 'outWork' ? 2 : 3
    this.newEvent = {
      id: event.id,
      title: event.title,
      summary: event.description,
      range: false,
      type: event.event_type === 'occurrence' ? 1 : type,
      perDay: false,
      allDay: allDay,
      dates: [event.start.date],
      time: {
        start: allDay ? null : moment(event.start_date).format('hh:mm a'),
        end: allDay ? null : moment(event.end_date).format('hh:mm a')
      },
      formId: event.form ? event.form.id : null,
      forms: null,
      locationId: event.location ? event.location.id : null,
      location: event.location ? event.location : null,
      locations: [],
      role: event.roleId ? event.roleId : null,
      sendNotification: false,
      guests: [],
      forOthers: event.created_by !== event.assigned_to || !!event.group_id,
      recurrence: null,
      meetLink: event.meetingLink,
      link: event.hyperlink,
      file: event.fileId ? event.file : null,
      usersId: [],
      recurrence_id: event.recurrence_id
    }
  }
}
